import { isAxiosError } from 'axios';
import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import { Pageable, PageResponse } from '@/entities/user/api/user.api';
import {
  toPartnerBusinessStatusFilter,
  type PartnerBusinessStatus,
} from '@/shared/utils/partnerBusinessStatus';

export interface PharmacySearchRequest {
  pharmacyNameSearchKey?: string;
  relatedHospSearchKey?: string;
  /**
   * 미전송 = 전체. OPEN | SUSPENDED | CLOSED.
   * 공공 인허가 연동 후 휴업(SUSPENDED)도 내려온다.
   */
  businessStatusFilter?: PartnerBusinessStatus;
}

export interface PharmacyInfoResponse {
  pharmacyId: number;
  pharmacyName: string;
  address: string;
  contactNumber: string;
  associatedClinics: string;
  /**
   * 폐업일(yyyy-MM-dd). 알 수 없으면 null.
   * 폐업 여부는 businessStatusType으로 판단한다.
   */
  closedOn?: string | null;
  /** 정상/휴업/폐업 판정값. 뱃지는 이 필드만 본다 */
  businessStatusType: PartnerBusinessStatus;
}

export interface PharmacyDetailResponse {
  pharmacyId: number;
  pharmacyUniqueCode: string;
  pharmacyName: string;
  address: string;
  contactNumber: string;
  remarks: string;
  longitude: number;
  latitude: number;
  /**
   * 폐업일(yyyy-MM-dd). 알 수 없으면 null.
   * 폐업 여부는 businessStatusType으로 판단한다.
   */
  closedOn?: string | null;
  /** 정상/휴업/폐업 판정값 */
  businessStatusType: PartnerBusinessStatus;
}

export interface PharmacyUpdateRequest {
  pharmacyName?: string;
  address?: string;
  contactNumber?: string;
  remarks?: string;
  longitude?: number;
  latitude?: number;
}

/** GET /admin/pharmacies/export 응답 */
export interface PharmacyExportResponse {
  fileName: string;
  downloadUrl: string;
  expiresAt: string;
  rowCount: number;
}

export const PHARMACY_EXPORT_EMPTY_CODE = 'PHARMACY_EXPORT_EMPTY';

/** 전체 건수 동기 생성이라 기본 10초보다 길게 잡는다 */
export const PHARMACY_EXPORT_TIMEOUT_MS = 900_000;

export const buildPharmacySearchParams = (
  searchRequest: PharmacySearchRequest,
): Record<string, string> => {
  const params: Record<string, string> = {};
  if (searchRequest.pharmacyNameSearchKey) {
    params.pharmacyNameSearchKey = searchRequest.pharmacyNameSearchKey;
  }
  if (searchRequest.relatedHospSearchKey) {
    params.relatedHospSearchKey = searchRequest.relatedHospSearchKey;
  }
  const businessStatusFilter = toPartnerBusinessStatusFilter(
    searchRequest.businessStatusFilter,
  );
  if (businessStatusFilter) {
    params.businessStatusFilter = businessStatusFilter;
  }
  return params;
};

/** 조건에 맞는 문전약국이 없으면 404 + PHARMACY_EXPORT_EMPTY */
export const isPharmacyExportEmptyError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === PHARMACY_EXPORT_EMPTY_CODE;
};

export const pharmacyService = {
  /**
   * 약국 목록 조회.
   * 레거시와 동일하게 flat 쿼리 파라미터 사용 (page는 0부터).
   */
  getPharmacies: async (
    searchRequest: PharmacySearchRequest,
    pageable: Pageable,
  ): Promise<PageResponse<PharmacyInfoResponse>> => {
    const params: Record<string, string | number> = {
      page: pageable.page,
      size: pageable.size,
      ...buildPharmacySearchParams(searchRequest),
    };

    const response = await apiClient.get(createApiPath.pharmacies.list(), {
      params,
    });
    return response.data;
  },

  /**
   * 문전약국 목록 엑셀.
   * 목록과 같은 필터로 서버가 파일을 만든 뒤 서명 URL만 돌려준다.
   */
  exportPharmacies: async (
    searchRequest: PharmacySearchRequest,
  ): Promise<PharmacyExportResponse> => {
    const response = await apiClient.get<PharmacyExportResponse>(
      createApiPath.pharmacies.export(),
      {
        params: buildPharmacySearchParams(searchRequest),
        timeout: PHARMACY_EXPORT_TIMEOUT_MS,
      },
    );
    return response.data;
  },

  getPharmacyDetail: async (
    pharmacyId: number,
  ): Promise<PharmacyDetailResponse> => {
    const response = await apiClient.get(
      createApiPath.pharmacies.detail(pharmacyId),
    );
    return response.data;
  },

  updatePharmacy: async (
    pharmacyId: number,
    updateData: PharmacyUpdateRequest,
  ): Promise<void> => {
    await apiClient.put(
      createApiPath.pharmacies.update(pharmacyId),
      updateData,
    );
  },
};
